<script lang="ts" setup>
import { Studio, StudioUser, SearchParams } from '@/modules/Studios/types'
import { ref, watch, onMounted } from 'vue'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import StudioMemberChip from '@/modules/Studios/components/tabs/StudioMemberChip.vue'
import { fetchStudioTeachers } from '@/enitites/studio/api/endpoints'
import Bugsnag from '@bugsnag/js'

const props = defineProps<{
  studio: Studio | undefined
}>()

const teachers = ref<StudioUser[]>([])
const loading = ref(false)
const sortBy = ref<'name'>('name')
const sortDirection = ref<'asc' | 'desc'>('asc')
const pageNumber = ref(0)
const pageSize = ref(100) // Adjust as needed
const keyword = ref('')

const fetchTeachers = async () => {
  if (!props.studio?.id) return

  loading.value = true
  try {
    const params: SearchParams = {
      pageNumber: pageNumber.value,
      pageSize: pageSize.value,
      keyword: keyword.value,
      sortBy: sortBy.value,
      sortDir: sortDirection.value,
    }

    const fetchedTeachers = await fetchStudioTeachers(props.studio.id, params)
    teachers.value = fetchedTeachers || []
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error fetching studio teachers',
        studioId: props.studio?.id,
        searchParams: {
          pageNumber: pageNumber.value,
          pageSize: pageSize.value,
          keyword: keyword.value,
          sortBy: sortBy.value,
          sortDir: sortDirection.value,
        },
      })
    })
    teachers.value = []
  } finally {
    loading.value = false
  }
}

watch([sortBy, sortDirection], () => {
  fetchTeachers()
})

watch(
  () => props.studio?.id,
  () => {
    if (props.studio?.id) {
      fetchTeachers()
    }
  },
  { immediate: true }
)

onMounted(() => {
  if (props.studio?.id) {
    fetchTeachers()
  }
})
</script>

<template>
  <div
    v-if="loading"
    class="placeholder"
  >
    <v-progress-circular
      indeterminate
      color="primary"
    />
  </div>
  <div
    v-else-if="teachers.length"
    class="tab-content"
  >
    <div class="filter-row">
      <div class="sort">
        <v-menu
          disabled
          offset-y
        >
          <template v-slot:activator="{ on }">
            <div
              class="sort-string"
              v-on="on"
            >
              Sort by:
              <span v-if="sortBy === 'name'"> Name </span>
            </div>
          </template>
          <v-list
            class="sort-menu"
            width="242"
          >
            <v-list-item>
              <span class="sort-title">Sort by</span>
            </v-list-item>

            <v-radio-group
              v-model="sortBy"
              class="mt-0"
            >
              <v-list-item
                class="d-flex justify-space-between"
                @click="sortBy = 'name'"
              >
                <span>Name</span>
                <div>
                  <v-radio
                    class="mr-0"
                    value="name"
                  />
                </div>
              </v-list-item>
            </v-radio-group>
          </v-list>
        </v-menu>
        <button
          class="sort-arrows"
          type="button"
          @click="sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'"
        >
          <v-icon class="icon-up">
            {{ mdiChevronUp }}
          </v-icon>
          <v-icon class="icon-down">
            {{ mdiChevronDown }}
          </v-icon>
        </button>
      </div>
    </div>
    <div class="members-list">
      <StudioMemberChip
        v-for="teacher in teachers"
        :key="teacher.id"
        :member="teacher"
      />
    </div>
  </div>
  <div
    v-else
    class="placeholder"
  >
    No teachers found for this studio
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  font-weight: var(--font-weight-bold);
  font-size: 18px;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 20px;
}

.filter-row {
  display: flex;
  gap: 24px;
  justify-content: space-between;
  width: 100%;

  @media (max-width: $screen-sm) {
    flex-direction: column;
  }

  .sort {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  .sort-arrows {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.members-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
